/* Copyright (C) Okahu Inc 2023-2024. All rights reserved. */
'use client';

import React from 'react';
import { Badge } from '@/ux-common/src/components/ui/badge';
import { sizeStyles } from '@/ux-common/src/utils/constants';
import { TraceBadgesProps } from 'types/traces';

import { cn } from '@/lib/utils';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { System } from '@/components/icons';

const getMeasureStyle = (measure: string) => {
  const key = measure?.toLowerCase() || '';
  if (key.includes('error')) {
    return {
      variant: 'destructive' as const,
      className:
        'h-6 cursor-default bg-notification-error-dark pl-2 pr-3 font-normal leading-4 text-white hover:bg-notification-error-dark',
      icon: System.InfoIcon('h-3.5 w-3.5'),
    };
  }
  if (key.includes('test failure')) {
    return {
      className:
        'h-6 cursor-default bg-[#F4EA50] pl-2 pr-3 text-[11px] font-normal leading-4 hover:bg-[#F4EA50]',
      icon: System.TestFailuresIcon('h-3.5 w-3.5'),
    };
  }
  if (key.includes('performance')) {
    return {
      className:
        'h-6 cursor-default bg-warning-dark pl-2 pr-3 text-[11px] font-normal leading-4 hover:bg-warning-dark',
      icon: System.FileErrorOutline('h-3.5 w-3.5'),
    };
  }
  if (key.includes('budget')) {
    return {
      className:
        'h-6 cursor-default bg-warning-dark pl-2 pr-3 text-[11px] font-normal leading-4 hover:bg-warning-dark',
      icon: System.BudgetIcon('h-3.5 w-3.5'),
    };
  }
  return {
    className:
      'h-6 cursor-default bg-[#1E3A3A] pl-2 pr-3 text-[11px] font-normal leading-4 text-white hover:bg-[#1E3A3A]',
    icon: null,
  };
};

const TraceBadges: React.FC<TraceBadgesProps> = ({ labels, loading }) => {
  const visibleLabels =
    labels?.filter((current) => current?.label?.trim().length) ?? [];

  if (loading) {
    return (
      <div className="flex items-center gap-2">
        <Skeleton
          className={cn(
            'animate-pulse-custom my-auto w-16 rounded-full bg-[#8C9EA1]',
            sizeStyles['default']
          )}
        />
      </div>
    );
  }

  if (!visibleLabels.length) {
    return null;
  }

  return (
    <div className="flex items-center gap-2">
      {visibleLabels.map((current: { label: string; tooltip: string }) => {
        const { className, icon } = getMeasureStyle(current.label);
        return (
          <TooltipProvider key={current.label}>
            <Tooltip>
              <TooltipTrigger asChild>
                <Badge className={cn(className, 'whitespace-nowrap')}>
                  {icon && <span className="mr-1">{icon}</span>}
                  {current.label}
                </Badge>
              </TooltipTrigger>
              {current.tooltip && (
                <TooltipContent
                  side="top"
                  align="center"
                  className="max-w-64 whitespace-normal break-words rounded px-3 py-2 text-xs leading-relaxed"
                >
                  <span>{current.tooltip}</span>
                </TooltipContent>
              )}
            </Tooltip>
          </TooltipProvider>
        );
      })}
    </div>
  );
};

export { TraceBadges };
